import React from 'react';
import { ArrowRightLeft, MapPin, MessageCircle, ExternalLink } from 'lucide-react';
import { TradeMatch } from '../../types/collection';

interface MatchCardProps {
  match: TradeMatch;
  onViewProfile: (uid: string) => void;
  onContact: (match: TradeMatch) => void;
}

export default function MatchCard({ match, onViewProfile, onContact }: MatchCardProps) {
  const { user, theyHaveYouWant, youHaveTheyWant, score } = match;

  const isMutual = youHaveTheyWant.length > 0;

  return (
    <div className={`
      bg-gray-900/50 rounded-xl p-4 border transition-colors
      ${isMutual
        ? 'border-green-500/30 hover:border-green-500/50'
        : 'border-gray-800 hover:border-gray-700'
      }
    `}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
            {user.handle?.[0]?.toUpperCase() || '?'}
          </div>
          <div>
            <button
              onClick={() => onViewProfile(user.uid)}
              className="font-semibold text-white hover:text-blue-400 transition-colors"
            >
              @{user.handle}
            </button>
            {user.location && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <MapPin className="w-3 h-3" />
                <span>{user.location.country}</span>
              </div>
            )}
          </div>
        </div>

        {isMutual && (
          <div className="flex items-center gap-1 px-2 py-1 bg-green-500/20 rounded-full">
            <ArrowRightLeft className="w-3 h-3 text-green-400" />
            <span className="text-xs font-medium text-green-400">Mutual</span>
          </div>
        )}
      </div>

      {/* Cards they have that you want */}
      <div className="mb-3">
        <p className="text-xs text-gray-400 mb-2">
          They have ({theyHaveYouWant.length} cards you want):
        </p>
        <div className="flex flex-wrap gap-1">
          {theyHaveYouWant.slice(0, 5).map(cardId => (
            <span
              key={cardId}
              className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded"
            >
              {cardId}
            </span>
          ))}
          {theyHaveYouWant.length > 5 && (
            <span className="px-2 py-1 bg-gray-800 text-gray-400 text-xs rounded">
              +{theyHaveYouWant.length - 5} more
            </span>
          )}
        </div>
      </div>

      {/* Cards you have that they want */}
      {youHaveTheyWant.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-400 mb-2">
            You have ({youHaveTheyWant.length} cards they want):
          </p>
          <div className="flex flex-wrap gap-1">
            {youHaveTheyWant.slice(0, 5).map(cardId => (
              <span
                key={cardId}
                className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded"
              >
                {cardId}
              </span>
            ))}
            {youHaveTheyWant.length > 5 && (
              <span className="px-2 py-1 bg-gray-800 text-gray-400 text-xs rounded">
                +{youHaveTheyWant.length - 5} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onViewProfile(user.uid)}
          className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          View Profile
        </button>
        <button
          onClick={() => onContact(match)}
          className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          Contact
        </button>
      </div>

      {/* Match score indicator */}
      <div className="mt-3 pt-3 border-t border-gray-800 flex items-center justify-between">
        <span className="text-xs text-gray-500">Match score</span>
        <div className="flex items-center gap-2">
          <div className="w-24 h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${
                score >= 50 ? 'bg-green-500' :
                score >= 25 ? 'bg-yellow-500' :
                'bg-gray-600'
              }`}
              style={{ width: `${Math.min(100, score)}%` }}
            />
          </div>
          <span className="text-xs text-gray-400 w-8">{score}</span>
        </div>
      </div>
    </div>
  );
}
